<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import Icon from "@iconify/svelte";
  import { Popover, Button, Dropdown, DropdownItem } from "flowbite-svelte";

  export let editor;
  export let compactMode = false;
  export let showFullMenu = true;
  export let showHtml = false;

  const dispatch = createEventDispatcher();
  let isTableActive = false;
  let hasTableHeader = false;
  let canUndo = false;
  let canRedo = false;
  // Initialize to false to ensure links don't open in new window by default
  let openLinksInNewWindow = false;
  let isLinkActive = false;
  let isExternalLink = false;

  const addImage = () => {
    const url = window.prompt("URL");
    if (url) {
      editor?.chain().focus().setImage({ src: url }).run();
    }
  };

  const addLink = () => {
    const previousUrl = editor?.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);
    if (url === null) {
      return;
    }

    if (url === "") {
      editor?.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    try {
      // Create link options with target only if openLinksInNewWindow is true
      const linkOptions = {
        href: url,
        target: openLinksInNewWindow ? "_blank" : null,
        rel: openLinksInNewWindow ? "noopener noreferrer" : null,
      };

      console.log("Adding link with options:", linkOptions);

      editor?.chain().focus().extendMarkRange("link").setLink(linkOptions).run();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "An unknown error occurred");
    }
  };

  const addYoutube = () => {
    const url = window.prompt("Enter YouTube URL");
    if (url) {
      editor
        ?.chain()
        .focus()
        .setYoutubeVideo({
          src: url,
          width: 640,
          height: 480,
        })
        .run();
    }
  };

  const updateStates = () => {
    if (editor) {
      isTableActive = editor.isActive("table");
      hasTableHeader = editor.isActive("table", { hasHeader: true });
      canUndo = editor.can().undo();
      canRedo = editor.can().redo();

      isLinkActive = editor.isActive("link");

      if (isLinkActive) {
        const linkAttrs = editor.getAttributes("link");
        isExternalLink = linkAttrs.target === "_blank";

        openLinksInNewWindow = isExternalLink;
      }
    }
  };

  $: if (editor) {
    updateStates();
    editor.on("selectionUpdate", updateStates);
    editor.on("transaction", updateStates);
  }
</script>

<div class="menu-bar">
  {#if editor}
    <div class="menu-group">
      <button
        on:click={() => editor.chain().focus().toggleBold().run()}
        class:active={editor?.isActive("bold")}
        title="Bold">
        <Icon icon="heroicons:bold" class="w-4 h-4" />
      </button>
      <button
        on:click={() => editor.chain().focus().toggleItalic().run()}
        class:active={editor?.isActive("italic")}
        title="Italic">
        <Icon icon="heroicons:italic" class="w-4 h-4" />
      </button>
      <button
        on:click={() => editor.chain().focus().toggleUnderline().run()}
        class:active={editor?.isActive("underline")}
        title="Underline">
        <Icon icon="heroicons:underline" class="w-4 h-4" />
      </button>
      <button
        on:click={() => editor.chain().focus().toggleStrike().run()}
        class:active={editor?.isActive("strike")}
        title="Strike">
        <Icon icon="heroicons:strikethrough" class="w-4 h-4" />
      </button>
    </div>

    <div class="menu-group">
      <button on:click={() => editor.commands.undo()} disabled={!canUndo} title="Undo">
        <Icon icon="heroicons:arrow-uturn-left" class="w-4 h-4" />
      </button>
      <button on:click={() => editor.commands.redo()} disabled={!canRedo} title="Redo">
        <Icon icon="heroicons:arrow-uturn-right" class="w-4 h-4" />
      </button>
    </div>

    <div class="menu-group">
      <button
        on:click={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        class:active={editor?.isActive("heading", { level: 1 })}>
        H1
      </button>
      <button
        on:click={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        class:active={editor?.isActive("heading", { level: 2 })}>
        H2
      </button>
      <button
        on:click={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        class:active={editor?.isActive("heading", { level: 3 })}>
        H3
      </button>
    </div>

    <div class="menu-group">
      <button
        on:click={() => editor.chain().focus().toggleBlockquote().run()}
        class:active={editor?.isActive("blockquote")}
        title="Quote">
        <Icon icon="mdi:format-quote-open" class="w-5 h-5" />
      </button>

      <!-- List dropdown -->
      <div class="relative inline-block">
        <button
          class="lists-button {editor?.isActive('bulletList') ||
          editor?.isActive('orderedList') ||
          editor?.isActive('fireList') ||
          editor?.isActive('prosList') ||
          editor?.isActive('consList')
            ? 'active'
            : ''}">
          <Icon icon="mdi:format-list-bulleted" class="w-5 h-5 text-black" />
          <Icon icon="heroicons:chevron-down" class="w-4 h-4 text-black" />
        </button>
        <Dropdown class="w-44" triggeredBy=".lists-button">
          <DropdownItem
            on:click={() => editor.chain().focus().toggleBulletList().run()}
            class="flex items-center {editor?.isActive('bulletList') ? 'bg-gray-100 dark:bg-gray-700' : ''}">
            <Icon icon="mdi:circle-medium" class="w-5 h-5 mr-2" />
            <span>Bullet List</span>
          </DropdownItem>
          <DropdownItem
            on:click={() => editor.chain().focus().toggleOrderedList().run()}
            class="flex items-center {editor?.isActive('orderedList') ? 'bg-gray-100 dark:bg-gray-700' : ''}">
            <Icon icon="mdi:123" class="w-5 h-5 mr-2" />
            <span>Numbered List</span>
          </DropdownItem>
          <DropdownItem
            on:click={() => editor.chain().focus().toggleGiftList().run()}
            class="flex items-center {editor?.isActive('giftList') ? 'bg-gray-100 dark:bg-gray-700' : ''}">
            <Icon color="#82b452" icon="mdi:gift-outline" class="w-5 h-5 mr-2" />
            <span>Gift List</span>
          </DropdownItem>
          <DropdownItem
            on:click={() => editor.chain().focus().toggleFireList().run()}
            class="flex items-center {editor?.isActive('fireList') ? 'bg-gray-100 dark:bg-gray-700' : ''}">
            <Icon color="#e53f38" icon="mdi:fire" class="w-5 h-5 mr-2" />
            <span>Fire List</span>
          </DropdownItem>
          <DropdownItem
            on:click={() => editor.chain().focus().toggleProsList().run()}
            class="flex items-center {editor?.isActive('prosList') ? 'bg-gray-100 dark:bg-gray-700' : ''}">
            <Icon color="#6acc2a" icon="mdi:check-circle" class="w-5 h-5 mr-2" />
            <span>Pros List</span>
          </DropdownItem>
          <DropdownItem
            on:click={() => editor.chain().focus().toggleConsList().run()}
            class="flex items-center {editor?.isActive('consList') ? 'bg-gray-100 dark:bg-gray-700' : ''}">
            <Icon color="#e53f38" icon="mdi:close-circle" class="w-5 h-5 mr-2" />
            <span>Cons List</span>
          </DropdownItem>
        </Dropdown>
      </div>
    </div>

    <div class="menu-group">
      <button
        on:click={() => editor.chain().focus().setTextAlign("left").run()}
        class:active={editor?.isActive({ textAlign: "left" })}
        title="Align Left">
        <Icon icon="mdi:format-align-left" class="w-4 h-4" />
      </button>
      <button
        on:click={() => editor.chain().focus().setTextAlign("center").run()}
        class:active={editor?.isActive({ textAlign: "center" })}
        title="Align Center">
        <Icon icon="mdi:format-align-center" class="w-4 h-4" />
      </button>
      <button
        on:click={() => editor.chain().focus().setTextAlign("right").run()}
        class:active={editor?.isActive({ textAlign: "right" })}
        title="Align Right">
        <Icon icon="mdi:format-align-right" class="w-4 h-4" />
      </button>
    </div>

    <div class="menu-group">
      <button on:click={addLink} class:active={isLinkActive} title="Add Link">
        <Icon icon="heroicons:link" class="w-4 h-4" />
      </button>
      <button on:click={() => editor.chain().focus().unsetLink().run()} disabled={!isLinkActive} title="Remove Link">
        <Icon icon="heroicons:link" class="w-4 h-4" />
      </button>
      <!-- <button
        on:click={toggleOpenInNewWindow}
        class:active={isExternalLink}
        disabled={!isLinkActive}
        title={openLinksInNewWindow ? "Links open in new window" : "Links open in same window"}>
        <Icon icon="heroicons:arrow-top-right-on-square" class="w-4 h-4" />
      </button> -->
    </div>

    <div class="menu-group">
      <!-- <button
        on:click={() => editor.chain().focus().toggleHighlight().run()}
        class:active={editor?.isActive("highlight")}
        title="Highlight">
        <Icon icon="heroicons:paint-brush-16-solid" class="w-4 h-4" />
      </button> -->
      <!-- <button
        on:click={() => editor.chain().focus().setColor("#ff0000").run()}
        class:active={editor?.isActive("textStyle", { color: "#ff0000" })}
        title="Red">
        <Icon icon="heroicons:paint-brush" class="w-4 h-4 text-red-500" />
      </button> -->
      <!-- <input
                type="color"
                placeholder="Noffa"
                class="mr-2"
                on:input={({ currentTarget }) =>
                    editor.chain().focus().setColor(currentTarget.value).run()
                }
                title="Text Color"
            /> -->
      <button on:click={() => editor.commands.unsetAllMarks()} title="Clear Formatting">
        <Icon icon="heroicons:no-symbol" class="w-4 h-4" />
      </button>
      <button on:click={() => dispatch("toggleHtml")} class:active={showHtml} title="View HTML">
        <Icon icon="heroicons:code-bracket" class="w-4 h-4" />
      </button>
    </div>

    <div class="menu-group">
      <button on:click={addImage} title="Add Image">
        <Icon icon="heroicons:photo" class="w-5 h-5" />
      </button>
      <button on:click={addYoutube} title="Add YouTube Video">
        <Icon icon="mdi:youtube" class="w-5 h-5" />
      </button>
    </div>

    <div class="menu-group">
      <button
        class="menu-item"
        on:click={() =>
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withTitle: true, withHeaderRow: true }).run()}
        title="Insert Table">
        <Icon icon="mdi:table-large-plus" class="w-4 h-4" />
      </button>
      <button
        class="menu-item"
        on:click={() => editor.chain().focus().deleteTable().run()}
        disabled={!isTableActive}
        title="Delete table">
        <Icon icon="mdi:table-large-remove" class="w-4 h-4" />
      </button>
      <button
        class="menu-item"
        on:click={() => editor.chain().focus().addColumnBefore().run()}
        disabled={!isTableActive}
        title="Add column before">
        <Icon icon="ooui:table-move-column-before-ltr" class="w-4 h-4" />
      </button>
      <button
        class="menu-item"
        on:click={() => editor.chain().focus().addColumnAfter().run()}
        disabled={!isTableActive}
        title="Add column after">
        <Icon icon="ooui:table-move-column-after-ltr" class="w-4 h-4" />
      </button>
      <button
        class="menu-item"
        on:click={() => editor.chain().focus().deleteColumn().run()}
        disabled={!isTableActive}
        title="Delete column">
        <Icon icon="mdi:table-column-remove" class="w-4 h-4" />
      </button>
      <button
        class="menu-item"
        on:click={() => editor.chain().focus().addRowBefore().run()}
        disabled={!isTableActive}
        title="Add row before">
        <Icon icon="ooui:table-move-row-before" class="w-4 h-4" />
      </button>
      <button
        class="menu-item"
        on:click={() => editor.chain().focus().addRowAfter().run()}
        disabled={!isTableActive}
        title="Add row after">
        <Icon icon="ooui:table-move-row-after" class="w-4 h-4" />
      </button>
      <button
        class="menu-item"
        on:click={() => editor.chain().focus().deleteRow().run()}
        disabled={!isTableActive}
        title="Delete row">
        <Icon icon="mdi:table-row-remove" class="w-4 h-4" />
      </button>
      <button
        class="menu-item"
        disabled={!isTableActive}
        title="Toggle Pre Table Title Div"
        on:click={() => editor.chain().focus().togglePreTableTitleDiv().run()}>
        <Icon icon="flowbite:table-row-outline" class="w-4 h-4" />
      </button>
    </div>

    {#if compactMode}
      <div class="menu-group menu-toggle">
        <button on:click={() => dispatch("toggleMenu")} title={showFullMenu ? "Show Less" : "Show More"}>
          {#if showFullMenu}
            <Icon icon="heroicons:chevron-up" class="w-4 h-4" />
          {:else}
            <Icon icon="heroicons:chevron-down" class="w-4 h-4" />
          {/if}
        </button>
      </div>
    {/if}
  {/if}
</div>

<style>
  .menu-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.5rem;
    background-color: #f8f9fa;
    border-radius: 0.25rem;
  }

  .menu-group {
    display: flex;
    gap: 0.25rem;
    padding: 0.25rem;
    border-right: 1px solid #dee2e6;
    flex-wrap: wrap;
  }

  .menu-group:last-child {
    border-right: none;
  }

  button,
  .button {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 2rem;
    padding: 0.375rem;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    color: #495057;
    cursor: pointer;
    transition: all 0.2s;
  }

  button:hover {
    background-color: #e9ecef;
    border-color: #dee2e6;
  }

  .active {
    background-color: #e9ecef;
    border-color: #ced4da;
    color: #212529;
  }

  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  :global(.table-header) {
    margin-bottom: 1rem;
    padding: 0.5rem;
    background-color: #f3f4f6;
    border-radius: 0.375rem;
  }

  :global(.table-title) {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #374151;
    outline: none;
  }

  :global(.table-title:empty::before) {
    content: attr(data-placeholder);
    color: #9ca3af;
  }
</style>
